import { createFileRoute } from '@tanstack/react-router'
import { AlternativesPage, alternativesHead, type AlternativesData } from '@/components/alternatives-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

const data: AlternativesData = {
  tool: "Checkly",
  slug: "checkly",
  intro: "Checkly is built for monitoring as code with Playwright. If you mainly need uptime checks, or want flat pricing without per-run overage, these alternatives cover a range of needs.",
  alternatives: [
    {
      name: "Uptrack",
      description: "If you use Checkly mainly for HTTP uptime checks, Uptrack runs them every 30 seconds on every plan, with alert confirmation and flat pricing from $3/mo.",
      pricing: "Free (5 monitors), Pro $3/mo, Team $20/mo, Business $79/mo",
      pros: ["30-second checks on every plan", "Alerts only after 2-5 consecutive failed checks", "Flat pricing with a 30-day money-back guarantee"],
      cons: ["Free plan is 5 monitors at 30 s from one location", MONITOR_LOCATIONS.alternativesCon, "No APM or logs"],
      url: "https://uptrack.app",
      isUptrack: true,
    },
    {
      name: "Hyperping",
      description: "Uptime monitoring, browser checks, on-call, and status pages, checked from 18 regions.",
      pricing: "Free (20 monitors), Essentials $24/mo",
      pros: ["Multi-region confirmation before an outage opens", "Browser checks and built-in on-call", "20-second checks on the Business plan"],
      cons: ["30-second checks start at $24/mo (Essentials)", "Extra seats cost $7-12/mo each"],
      url: "https://hyperping.com",
    },
    {
      name: "Better Stack",
      description: "Uptime monitoring combined with on-call, incident management, logs, and status pages in one platform.",
      pricing: "Free (10 monitors), paid from $34/mo per responder",
      pros: ["On-call scheduling and incident management", "Unlimited phone and SMS alerts on paid plans", "Multi-location confirmation (3 of 4 locations by default)"],
      cons: ["Priced per responder, plus monitor packs", "More product than you need if you only want uptime checks"],
      url: "https://betterstack.com",
    },
    {
      name: "Datadog",
      description: "Full observability platform covering APM, logs, infrastructure, and synthetic monitoring, billed by usage.",
      pricing: "Synthetics: $5 per 10k API runs, $12 per 1k browser runs (annual billing)",
      pros: ["Full-stack observability in one platform", "API and browser synthetic tests", "Retries and multi-location alert rules"],
      cons: ["No free tier for Synthetics", "Usage-based billing varies month to month"],
      url: "https://www.datadoghq.com",
    },
    {
      name: "Grafana Cloud",
      description: "Observability stack built on open-source tools (Prometheus, Loki, Tempo), with k6-based synthetic monitoring.",
      pricing: "Free tier, Pro from $19/mo plus usage",
      pros: ["Free tier with 100k API test executions a month", "Custom dashboards", "Built on open-source tools"],
      cons: ["Usage-based billing across several dimensions", "Scripted checks use k6 (JavaScript)"],
      url: "https://grafana.com/products/cloud/",
    },
  ],
  comparisonTable: [
    { tool: "Uptrack", freePlan: "5 monitors at 30s", checkInterval: "30s (all plans)", alertConfirmation: "Yes (2-5 consecutive)", startingPrice: "Free; Pro $3/mo" },
    { tool: "Hyperping", freePlan: "20 monitors", checkInterval: "5min (free), 30s (Essentials)", alertConfirmation: "Yes (multi-region)", startingPrice: "$24/mo" },
    { tool: "Better Stack", freePlan: "10 monitors", checkInterval: "3min (free), 30s (paid)", alertConfirmation: "Yes (multi-location)", startingPrice: "$34/mo per responder" },
    { tool: "Datadog", freePlan: "None (synthetics)", checkInterval: "Per test", alertConfirmation: "Configurable", startingPrice: "$5 per 10k API runs" },
    { tool: "Grafana Cloud", freePlan: "100k API executions", checkInterval: "10s and up", alertConfirmation: "Configurable", startingPrice: "$19/mo + usage" },
  ],
}

export const Route = createFileRoute('/_site/alternatives/checkly')({
  component: () => <AlternativesPage data={data} />,
  head: () => alternativesHead(data),
})
